import {
	faBuilding,
	faCheck,
	faCopy,
	faDatabase,
	faShieldCheck,
	faShieldXmark,
	faTableList,
} from "@fortawesome/pro-regular-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";

import { CompanyUploadPreview, JobTitleScreeningPreview } from "@signalist/shared-types";

import { CsvEnrichmentType } from "@/enum/csv-enrichment.enum";

import { RowLevelStats, toCount } from "./titleGate.utils";

export interface StatisticsRow {
	/** i18n key under `csvEnrichment.titleGate`, and the row's `data-test` suffix. */
	key: string;
	icon: IconDefinition;
	iconClass: string;
	value: number;
}

interface BuildXlsStatisticsRowsArgs {
	entityType?: string | null;
	rowStats: RowLevelStats;
	preview?: JobTitleScreeningPreview;
	companyPreview?: CompanyUploadPreview;
}

/**
 * PREC-3870 — which rows the XLS Statistics box shows, as a pure function of the upload.
 *
 * The three row-level figures are a property of the file and always show. The trailing pair is
 * whatever that list type can say about its contents: job-title verdicts for a people upload,
 * known-vs-new companies for a company upload. A pair whose preview has not resolved is **omitted
 * rather than zeroed** — "0 companies already in Signalist" and "we have not asked yet" are
 * different statements, and only one of them is true while the dry-run is in flight.
 */
export const buildXlsStatisticsRows = ({
	entityType,
	rowStats,
	preview,
	companyPreview,
}: BuildXlsStatisticsRowsArgs): StatisticsRow[] => [
	{
		key: "rowsDetected",
		icon: faTableList,
		iconClass: "bg-gray-100 text-text-meta",
		value: rowStats.rowsDetected,
	},
	{
		key: "rowsReady",
		icon: faCheck,
		iconClass: "bg-status-success-light text-status-success",
		value: rowStats.rowsReady,
	},
	{
		key: "duplicates",
		icon: faCopy,
		iconClass: "bg-status-warning-light text-status-running",
		value: rowStats.duplicates,
	},
	...trailingRows({ entityType, preview, companyPreview }),
];

const trailingRows = ({
	entityType,
	preview,
	companyPreview,
}: Omit<BuildXlsStatisticsRowsArgs, "rowStats">): StatisticsRow[] => {
	if (entityType === CsvEnrichmentType.ORGANIZATION) {
		return companyPreview
			? [
					{
						key: "alreadyKnownCompanies",
						icon: faDatabase,
						iconClass: "bg-status-success-light text-status-success",
						value: toCount(companyPreview.alreadyKnown),
					},
					{
						key: "newCompanies",
						icon: faBuilding,
						iconClass: "bg-gray-100 text-text-meta",
						value: toCount(companyPreview.newCompanies),
					},
				]
			: [];
	}

	return preview
		? [
				{
					key: "alreadyWhitelisted",
					icon: faShieldCheck,
					iconClass: "bg-status-success-light text-status-success",
					value: toCount(preview.alreadyWhitelisted),
				},
				{
					key: "alreadyBlacklisted",
					icon: faShieldXmark,
					iconClass: "bg-status-error-light text-status-error",
					value: toCount(preview.alreadyExcluded),
				},
			]
		: [];
};
